import { Controller } from "@hotwired/stimulus"

// Custom select for membership roles. Renders a pill trigger with a colored
// dot + label and a popover of options, each with its own dot. Selecting an
// option updates the hidden input, repaints the trigger, and submits the
// parent form (so the existing Rails update flow handles the redirect).
//
// Markup:
//   <div data-controller="role-select">
//     <input type="hidden" name="…[role]" value="…" data-role-select-target="input">
//     <button type="button" data-action="click->role-select#toggle">
//       <span data-role-select-target="triggerDot" class="…"></span>
//       <span data-role-select-target="triggerLabel">…</span>
//     </button>
//     <div data-role-select-target="menu" hidden>
//       <button type="button" data-action="click->role-select#select" data-value="member">…</button>
//       …
//     </div>
//   </div>
const DOT_CLASSES = {
  member: "bg-app-text-faint",
  admin: "bg-vivid-purple",
}

const LABELS = {
  member: "Member",
  admin: "Admin",
}

export default class extends Controller {
  static targets = ["input", "triggerDot", "triggerLabel", "menu", "option"]

  connect() {
    this._closeOnOutside = (event) => {
      if (!this.element.contains(event.target)) this.close()
    }
    this._closeOnEscape = (event) => {
      if (event.key === "Escape") this.close()
    }
  }

  disconnect() {
    document.removeEventListener("click", this._closeOnOutside)
    document.removeEventListener("keydown", this._closeOnEscape)
  }

  toggle(event) {
    event?.preventDefault()
    if (this.menuTarget.hidden) this.open()
    else this.close()
  }

  open() {
    this.menuTarget.hidden = false
    document.addEventListener("click", this._closeOnOutside)
    document.addEventListener("keydown", this._closeOnEscape)
  }

  close() {
    this.menuTarget.hidden = true
    document.removeEventListener("click", this._closeOnOutside)
    document.removeEventListener("keydown", this._closeOnEscape)
  }

  select(event) {
    event?.preventDefault()
    const value = event.currentTarget.dataset.value
    if (!value) return
    if (this.inputTarget.value === value) {
      this.close()
      return
    }

    this.inputTarget.value = value
    this.triggerLabelTarget.textContent = LABELS[value] || value
    this._setDotClass(this.triggerDotTarget, value)

    this.optionTargets.forEach((opt) => {
      const isCurrent = opt.dataset.value === value
      opt.dataset.current = isCurrent ? "true" : "false"
    })

    this.close()
    this.element.closest("form")?.requestSubmit()
  }

  _setDotClass(el, role) {
    Object.values(DOT_CLASSES).forEach((c) => el.classList.remove(c))
    const next = DOT_CLASSES[role] || DOT_CLASSES.member
    el.classList.add(next)
  }
}
